import { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { supabase } from '@/integrations/supabase/client';
import { shouldEnableRealtime } from '@/utils/realtimeGuard';

interface Destination {
  id: string;
  name: string;
  status: string;
  points_required?: number;
}

const SimplifiedDestinationMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [destinations, setDestinations] = useState<Destination[]>([]);

useEffect(() => {
  fetchDestinations();
  
  // Set up real-time subscription (guarded)
  let channel: any;
  if (shouldEnableRealtime()) {
    channel = supabase
      .channel('destinations-changes-home')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'destinations'
      }, () => {
        fetchDestinations();
      })
      .subscribe();
  }

  return () => {
    if (channel) supabase.removeChannel(channel);
  };
}, []);

  const fetchDestinations = async () => {
    try {
      const { data, error } = await supabase
        .from('destinations')
        .select('*')
        .eq('status', 'open')
        .order('date_created', { ascending: false });

      if (error) throw error;
      setDestinations(data || []);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    }
  };

  useEffect(() => {
    if (destinations.length > 0) {
      initMap();
    }
  }, [destinations]);

  const initMap = async () => {
    try {
      // Récupération sécurisée de la clé API via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('get-google-maps-key');
        
        if (error) {
          console.error('Erreur Supabase:', error);
          throw new Error('Erreur lors de la récupération de la clé API');
        }
        
        if (!data?.apiKey) {
          throw new Error('Clé API Google Maps non disponible');
        }

        const loader = new Loader({
          apiKey: data.apiKey,
          version: 'weekly',
          libraries: ['places']
        });

        const google = await loader.load();
        
        if (!mapRef.current) return;

        // Configuration de la carte simplifiée
        const map = new google.maps.Map(mapRef.current, {
          zoom: 3,
          center: { lat: 45.0, lng: 10.0 }, // Centré sur l'Europe
          styles: [
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#e3f2fd' }]
            },
            {
              featureType: 'landscape',
              elementType: 'geometry',
              stylers: [{ color: '#f5f5f5' }]
            },
            {
              featureType: 'administrative',
              elementType: 'geometry.stroke',
              stylers: [{ color: '#c9c9c9' }, { weight: 0.8 }]
            }
          ],
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative'
        });

        // Ajout des marqueurs avec geocoding pour chaque destination
        const geocoder = new google.maps.Geocoder();
        
        destinations.forEach(async (destination) => {
          try {
            const geocodeResult = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
              geocoder.geocode({ address: destination.name }, (results, status) => {
                if (status === 'OK' && results) {
                  resolve(results);
                } else {
                  reject(new Error(`Geocoding failed: ${status}`));
                }
              });
            });

            if (geocodeResult && geocodeResult[0]) {
              const location = geocodeResult[0].geometry.location;
              
              const marker = new google.maps.Marker({
                position: location,
                map: map,
                title: destination.name,
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: destination.status === 'open' ? '#4275B3' : '#9CA3AF',
                  fillOpacity: 1,
                  strokeWeight: 2,
                  strokeColor: '#ffffff'
                }
              });

              // InfoWindow simple et élégante
              const infoWindow = new google.maps.InfoWindow({
                content: `
                  <div style="padding: 8px; font-family: system-ui;">
                    <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 16px; font-weight: 600;">${destination.name}</h3>
                    <p style="margin: 0 0 4px 0; color: #6b7280; font-size: 14px;">Status: ${destination.status}</p>
                    <p style="margin: 0; color: #4275B3; font-size: 14px; font-weight: 500;">${destination.points_required || 50} pts/mois</p>
                  </div>
                `
              });

              marker.addListener('click', () => {
                infoWindow.open(map, marker);
              });
            }
          } catch (error) {
            console.error(`Error geocoding ${destination.name}:`, error);
          }
        });

        setIsLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement de la carte:', error);
        setError('Impossible de charger la carte des destinations');
        setIsLoading(false);
      }
    };

  if (error) {
    return (
      <div className="w-full h-full bg-muted flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-2">🗺️</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 bg-muted flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Chargement des destinations...</p>
          </div>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
};

export default SimplifiedDestinationMap;